import ItemPicker from '../../shared/components/ItemPicker';
import { useEditProtection } from '../../shared/components/EditProtection';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createReceivingInvoice, getReceivingInvoice, updateReceivingInvoice, listSuppliers, listItems, Supplier, InventoryItem } from './api';
import './inventory-v2.css';
import DataTable from '../../shared/components/DataTable';

interface LineItem {
  raw_item_name: string;
  inventory_item_id: number | null;
  quantity_received: string;
  unit: string;
  purchase_cost: string;
  notes: string;
}

const ReceivingForm: React.FC = () => {
  const { sessionToken } = useAuth();
  const navigate = useNavigate();
  const {id}=useParams<{id:string}>();
  const [pending,setPending]=useState<any>(null);
  const saveBusy=useRef(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    stock_effect: 'receive_now',
    supplier_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    total: '',
    tax: '',
    discount: '',
    notes: '',
  });

  const [lines, setLines] = useState<LineItem[]>([
    { raw_item_name: '', inventory_item_id: null, quantity_received: '', unit: 'count', purchase_cost: '', notes: '' },
  ]);

  const [saved, setSaved] = useState(false);
  useEditProtection(!saved && (!!form.invoice_number || !!form.notes || lines.some(l => !!l.raw_item_name || !!l.quantity_received || !!l.purchase_cost || !!l.inventory_item_id)));
  useEffect(() => {
    Promise.all([listSuppliers(sessionToken), listItems(sessionToken), id?getReceivingInvoice(sessionToken,Number(id)):Promise.resolve(null)])
      .then(([s, i, existing]) => {
        setSuppliers(s.suppliers); setItems(i.items);
        if(existing) {
          if(existing.invoice.status!=='draft') {navigate(`/inventory/receiving/${id}`,{replace:true});return;}
          const invoice=existing.invoice;
          setForm({stock_effect:invoice.stock_effect || 'receive_now',supplier_id:String(invoice.supplier_id || ''),invoice_number:invoice.invoice_number || '',invoice_date:invoice.invoice_date?.slice(0,10) || '',total:'',tax:String(invoice.tax || 0),discount:String(invoice.discount || 0),notes:invoice.notes || ''});
          setLines(existing.lines.map(line=>({raw_item_name:line.raw_item_name || '',inventory_item_id:line.inventory_item_id,quantity_received:String(line.quantity_received),unit:line.unit,purchase_cost:String(line.purchase_cost),notes:line.notes || ''})));
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [sessionToken,id,navigate]);

  const addLine = () => {
    setLines(prev => [...prev, { raw_item_name: '', inventory_item_id: null, quantity_received: '', unit: 'count', purchase_cost: '', notes: '' }]);
  };

  const removeLine = (idx: number) => {
    setLines(prev => prev.filter((_, i) => i !== idx));
  };

  const updateLine = (idx: number, field: string, value: any) => {
    setLines(prev => prev.map((line, i) => i === idx ? { ...line, [field]: value } : line));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if(saveBusy.current)return;
    saveBusy.current=true;
    setError('');
    setSaving(true);

    try {
      const body = pending || {
        request_id:crypto.randomUUID(),
        stock_effect:form.stock_effect,
        supplier_id: form.supplier_id ? Number(form.supplier_id) : null,
        invoice_number: form.invoice_number || null,
        invoice_date: form.invoice_date || null,
        total: form.total ? Number(form.total) : null,
        tax: form.tax ? Number(form.tax) : null,
        discount: form.discount ? Number(form.discount) : null,
        notes: form.notes || null,
        lines: lines.filter(l => l.quantity_received && l.purchase_cost).map(l => ({
          raw_item_name: l.raw_item_name || null,
          inventory_item_id: l.inventory_item_id || null,
          matched_item_name: l.inventory_item_id ? items.find(i => i.id === l.inventory_item_id)?.name : null,
          quantity_received: Number(l.quantity_received),
          unit: l.unit,
          purchase_cost: Number(l.purchase_cost),
          notes: l.notes || null,
        })),
      };

      setPending(body);
      const { invoice } = id?await updateReceivingInvoice(sessionToken,Number(id),body):await createReceivingInvoice(sessionToken, body);
      setSaved(true);
      setTimeout(() => navigate(`/inventory/receiving/${invoice.id}`), 0);
    } catch (e: any) {
      if(e.status>=400 && e.status<500)setPending(null);
      setError(e.message);
    } finally {
      saveBusy.current=false;
      setSaving(false);
    }
  };

  if (loading) return <div className="inv2-loading">Loading...</div>;

  const subtotal = lines.reduce((sum, l) => sum + (Number(l.purchase_cost) || 0), 0);

  return (
    <div className="inv2-page">
      <div className="inv2-header">
        <div>
          <Link to="/inventory/receiving" className="inv2-back-link">← Back to Receiving</Link>
          <h1>{id?'Edit Invoice Draft':'New Receiving Invoice'}</h1>
        </div>
      </div>

      {error && <div className="inv2-error">{error}</div>}

      <form onSubmit={handleSubmit} className="inv2-form">
        <fieldset disabled={saving || !!pending} style={{border:0,padding:0,minWidth:0}}>
        {/* Invoice Header */}
        <div className="inv2-form-grid">
          <div className="inv2-form-group">
            <label>Supplier</label>
            <select required value={form.supplier_id} onChange={e => setForm(prev => ({ ...prev, supplier_id: e.target.value }))} className="inv2-select">
              <option value="">— Select supplier —</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="inv2-form-group">
            <label>Invoice Number</label>
            <input required type="text" value={form.invoice_number} onChange={e => setForm(prev => ({ ...prev, invoice_number: e.target.value }))} className="inv2-input" />
          </div>
          <div className="inv2-form-group">
            <label>Invoice Date</label>
            <input required type="date" value={form.invoice_date} onChange={e => setForm(prev => ({ ...prev, invoice_date: e.target.value }))} className="inv2-input" />
          </div>
          <div className="inv2-form-group">
            <label>Tax ($)</label>
            <input inputMode="decimal" type="number" step="0.01" value={form.tax} onChange={e => setForm(prev => ({ ...prev, tax: e.target.value }))} className="inv2-input" />
          </div>
          <div className="inv2-form-group">
            <label>Discount ($)</label>
            <input inputMode="decimal" type="number" step="0.01" value={form.discount} onChange={e => setForm(prev => ({ ...prev, discount: e.target.value }))} className="inv2-input" />
          </div>
          <div className="inv2-form-group">
            <label>Notes</label>
            <input type="text" value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} className="inv2-input" />
          </div>
        </div>

        {/* Line Items */}
        <div className="inv2-section">
          <div className="inv2-section-header">
            <h2>Line Items</h2>
            <button type="button" onClick={addLine} className="inv2-btn">+ Add Line</button>
          </div>

          <div className="inv2-table-container">
            <DataTable
              rows={lines}
              rowKey={(_line, idx) => idx}
              caption="Invoice lines"
              moreLabel="Unit & price"
              empty="No lines yet"
              columns={[
                {
                  key: 'raw', header: 'Item Name (from invoice)', priority: 'primary',
                  render: (line, idx) => (
                    <input type="text" aria-label="Item as on invoice" value={line.raw_item_name}
                      onChange={e => updateLine(idx, 'raw_item_name', e.target.value)}
                      placeholder="Item as on invoice" className="inv2-input inv2-input-sm" />
                  ),
                },
                {
                  key: 'match', header: 'Match to Inventory', priority: 'summary',
                  render: (line, idx) => (
                    <ItemPicker value={line.inventory_item_id} items={items} onChange={value => updateLine(idx, 'inventory_item_id', value)} />
                  ),
                },
                {
                  key: 'qty', header: 'Quantity', priority: 'summary', numeric: true,
                  render: (line, idx) => (
                    <input inputMode="decimal" type="number" step="any" aria-label="Quantity" value={line.quantity_received}
                      onChange={e => updateLine(idx, 'quantity_received', e.target.value)} className="inv2-input inv2-input-sm" />
                  ),
                },
                {
                  key: 'cost', header: 'Cost ($)', priority: 'summary', numeric: true,
                  render: (line, idx) => (
                    <input inputMode="decimal" type="number" step="0.01" aria-label="Cost" value={line.purchase_cost}
                      onChange={e => updateLine(idx, 'purchase_cost', e.target.value)} className="inv2-input inv2-input-sm" />
                  ),
                },
                {
                  key: 'unit', header: 'Unit', priority: 'detail',
                  render: (line, idx) => (
                    <input type="text" aria-label="Unit" value={line.unit}
                      onChange={e => updateLine(idx, 'unit', e.target.value)} className="inv2-input inv2-input-sm" />
                  ),
                },
                {
                  key: 'uprice', header: 'Unit Price', priority: 'detail', numeric: true,
                  render: (line) => (
                    <span className="inv2-calculated-value">
                      ${line.purchase_cost && line.quantity_received
                        ? (Number(line.purchase_cost) / Number(line.quantity_received)).toFixed(4) : '—'}
                    </span>
                  ),
                },
              ]}
              actions={(_line, idx) => (
                lines.length > 1
                  ? <button type="button" onClick={() => removeLine(idx)} className="inv2-btn inv2-btn-sm inv2-btn-danger">× Remove line</button>
                  : null
              )}
            />
          </div>

          <div className="inv2-invoice-totals">
            <span>Subtotal: <strong>${subtotal.toFixed(2)}</strong></span>
          </div>
        </div>

        <label className="inv2-label">Stock treatment<select className="inv2-select" value={form.stock_effect} onChange={e=>setForm(old=>({...old,stock_effect:e.target.value}))}><option value="receive_now">New delivery — add to stock</option><option value="history_only">Already counted — price history only</option></select></label>
        </fieldset>
        {pending && !saving && <p>Retry keeps this invoice unchanged and prevents duplicate creation.</p>}
        <p>Enter total line costs after line discounts and before tax. Saving creates a draft. Stock changes only after you review and post the invoice.</p>
        <div className="inv2-form-actions cs-sticky">
          <button type="submit" disabled={saving} className="inv2-btn inv2-btn-primary">
            {saving ? 'Saving...' : pending ? 'Retry same invoice' : 'Save Draft Invoice'}
          </button>
          <Link to="/inventory/receiving" className="inv2-btn">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

export default ReceivingForm;
